import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/projects/components/ui/dialog";
import { Button } from "@/projects/components/ui/button";
import { Label } from "@/projects/components/ui/label";
import { Textarea } from "@/projects/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/projects/components/ui/select";
import { useSubmissions, useTeachers } from "@/projects/lib/useStore";
import { assignTeacher, teacherLoad } from "@/projects/lib/store";
import { toast } from "sonner";
import { StatusBadge } from "./status-badge";

export function AssignTeacherModal({
  submissionIds,
  open,
  onClose,
}: {
  submissionIds: string[] | null;
  open: boolean;
  onClose: () => void;
}) {
  const submissions = useSubmissions();
  const teachers = useTeachers();
  const [teacherId, setTeacherId] = useState<string>("");
  const [notes, setNotes] = useState("");

  const subs = useMemo(
    () => submissions.filter((s) => submissionIds?.includes(s.id)),
    [submissions, submissionIds],
  );
  const isBulk = subs.length > 1;
  const single = subs[0];

  const activeTeachers = teachers.filter((t) => t.active);

  const handleAssign = () => {
    if (!teacherId) { toast.error("Please select a teacher."); return; }
    assignTeacher(subs.map((s) => s.id), teacherId, notes);
    toast.success(isBulk ? `Assigned ${subs.length} submissions successfully` : "Teacher assigned successfully");
    setTeacherId(""); setNotes("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isBulk ? `Assign Teacher to ${subs.length} Submissions` : "Assign Teacher"}</DialogTitle>
          <DialogDescription>
            {isBulk
              ? "Select a teacher to assign all selected submissions."
              : "Choose a teacher to verify this student's submission."}
          </DialogDescription>
        </DialogHeader>

        {!isBulk && single && (
          <div className="space-y-1 rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Student</span><span className="font-medium">{single.studentName} · {single.studentId}</span></div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Video Title</span><span className="font-medium">{single.videoTitle}</span></div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Submitted</span><span>{new Date(single.submittedAt).toLocaleString()}</span></div>
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Current Status</span><StatusBadge status={single.status} /></div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label>Select Teacher</Label>
            <Select value={teacherId} onValueChange={setTeacherId}>
              <SelectTrigger><SelectValue placeholder="Choose a teacher" /></SelectTrigger>
              <SelectContent>
                {activeTeachers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <div className="flex w-full items-center gap-2">
                      <span className="font-medium">{t.name}</span>
                      <span className="text-xs text-muted-foreground">· {t.id} · {t.subject}</span>
                      <span className="ml-auto rounded-full bg-royal-soft px-1.5 py-0.5 text-[10px] font-medium text-royal">{teacherLoad(t.id)} active</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Assignment Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any note for the assigned teacher" rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAssign}>{isBulk ? `Assign ${subs.length}` : "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
